import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CodingWorkAPI as api, CodingAPI, type CodingSurvey, type Codebook, type CodingDecision } from '../../api/coding';
import { Card, ErrorBanner, InfoBanner, OkBanner } from '../Layout';

function safeUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function QuestionSampleResponses({ surveyId, variable }: { surveyId: number; variable: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['coding-samples-work', surveyId, variable],
    queryFn: () => CodingAPI.sampleResponses(surveyId, variable, 15),
  });

  if (isLoading) {
    return <div className="p-3 bg-plano text-xs text-tinta-2 animate-pulse rounded border border-grid">Cargando respuestas reales de ODK…</div>;
  }
  if (error) {
    return <ErrorBanner message={`Error al consultar respuestas de ODK: ${(error as Error).message}`} />;
  }
  if (!data?.items.length) {
    return (
      <div className="p-3 bg-plano text-xs text-tinta-3 rounded border border-grid">
        No se encontraron respuestas registradas en ODK para esta variable.
      </div>
    );
  }

  return (
    <div className="p-3 bg-plano rounded border border-grid space-y-2 text-xs">
      <div className="flex justify-between items-center text-tinta-2 font-semibold pb-1 border-b border-grid">
        <span>Respuestas reales registradas en campo (muestra de {data.items.length} casos)</span>
        <span className="text-[10px] text-tinta-3">Fuente: ODK Central</span>
      </div>
      <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1">
        {data.items.map((item, idx) => (
          <div key={item.case_id || idx} className="p-2 bg-superficie rounded border border-grid/60 flex items-start gap-2">
            <span className="font-mono text-[10px] text-tinta-3 whitespace-nowrap">#{item.case_id}</span>
            <p className="text-tinta break-words flex-1 font-sans">{item.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CodingWork({
  survey,
  books,
  section = 'all',
  onNavigate,
}: {
  survey: CodingSurvey;
  books: Codebook[];
  section?: 'runs' | 'review' | 'releases' | 'all';
  onNavigate?: (tab: string) => void;
}) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<number[]>([]);
  const [mode, setMode] = useState('new_changed');
  const [useAI, setAI] = useState(false);
  const [runId, setRunId] = useState('');
  const [binding, setBinding] = useState<string>(() => {
    return survey.bindings.length > 0 ? String(survey.bindings[0].id) : '';
  });
  const [offset, setOffset] = useState(0);
  const [pendingOnly, setPending] = useState(false);
  const [review, setReview] = useState<CodingDecision | null>(null);
  const [reason, setReason] = useState('');
  const [releaseName, setReleaseName] = useState('');
  const [acceptUnreviewed, setAcceptUnreviewed] = useState(false);
  const [acceptIncomplete, setAcceptIncomplete] = useState(false);
  const [releaseReason, setReleaseReason] = useState('');
  const [releaseId, setReleaseId] = useState('');
  const [base, setBase] = useState('validas');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    if (survey.bindings.length > 0) {
      if (!binding || !survey.bindings.some((b) => b.id === Number(binding))) {
        setBinding(String(survey.bindings[0].id));
      }
    } else {
      setBinding('');
    }
  }, [survey.bindings, binding]);

  const can = (c: string) => survey.capabilities.includes(c);
  const request = () => ({ binding_ids: selected.length ? selected : null, mode, use_ai: useAI, request_key: safeUUID() });

  const estimate = useMutation({ mutationFn: () => api.estimate(survey.id, request()) });
  const runQ = useQuery({
    queryKey: ['coding-run', runId],
    queryFn: () => api.run(runId),
    enabled: !!runId,
    refetchInterval: (q) => (['queued', 'running', 'paused'].includes(q.state.data?.status ?? '') ? 2000 : false),
  });
  const runs = useQuery({
    queryKey: ['coding-runs', survey.id],
    queryFn: () => api.runs(survey.id),
    enabled: can('code'),
    refetchInterval: (q) => {
      const hasActive = q.state.data?.some((r) => ['queued', 'running', 'paused'].includes(r.status));
      return hasActive ? 2500 : 10000;
    },
  });

  const activeRun = runs.data?.find((r) => ['queued', 'running', 'paused'].includes(r.status));

  useEffect(() => {
    if (activeRun && (!runId || !runs.data?.some((r) => r.id === runId && ['queued', 'running', 'paused'].includes(r.status)))) {
      setRunId(activeRun.id);
    }
  }, [activeRun?.id, runId, runs.data]);

  const handleCancelActive = async () => {
    submit(async () => {
      const res = await api.cancelActive(survey.id);
      void qc.invalidateQueries({ queryKey: ['coding-runs'] });
      void qc.invalidateQueries({ queryKey: ['coding-run'] });
      setNotice('Ejecución activa cancelada con éxito.');
      return res;
    });
  };

  const decisionQ = useQuery({
    queryKey: ['coding-decisions', binding, pendingOnly, offset],
    queryFn: () => api.decisions(Number(binding), pendingOnly ? 'pending' : '', offset),
    enabled: !!binding && can('originals'),
  });

  const releases = useQuery({ queryKey: ['coding-releases', survey.id], queryFn: () => api.releases(survey.id) });
  const frequencies = useQuery({
    queryKey: ['coding-frequencies', releaseId, binding, base],
    queryFn: () => api.frequencies(Number(releaseId), { binding_id: Number(binding), base }),
    enabled: !!releaseId && !!binding,
  });

  const mutation = useMutation({
    mutationFn: (job: () => Promise<unknown>) => job(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['coding-runs'] });
      void qc.invalidateQueries({ queryKey: ['coding-run'] });
      void qc.invalidateQueries({ queryKey: ['coding-decisions'] });
      void qc.invalidateQueries({ queryKey: ['coding-releases'] });
      void qc.invalidateQueries({ queryKey: ['coding-surveys'] });
      setNotice('Operación completada con éxito.');
    },
  });

  const activeBinding = survey.bindings.find((b) => b.id === Number(binding));
  const matrix = books.flatMap((b) => b.versions).find((v) => v.id === activeBinding?.version_id)?.specification;
  const reviewCodes = activeBinding?.review_codes ?? matrix?.codes;

  // Mapa de códigos para mostrar etiqueta junto con valor
  const codeLabels = (reviewCodes ?? []).reduce((acc: Record<string, string>, c) => {
    acc[c.value] = c.label || c.definition || c.value;
    return acc;
  }, {});

  const stateLabels: Record<string, string> = {
    VALIDA: 'Respuesta válida',
    NS: 'No sabe',
    NINGUNO: 'Ninguno',
    RECHAZO: 'No desea responder',
    VACIA: 'Sin respuesta',
    NO_APLICA: 'No aplica',
    SIN_CATEGORIA: 'Sin categoría asignada',
    PENDIENTE: 'Pendiente de revisión',
  };

  const methodLabels: Record<string, string> = {
    rules: 'Reglas de la matriz',
    memory: 'Revisión anterior reutilizada',
    ai: 'Inteligencia artificial',
    manual: 'Revisión manual',
    unresolved: 'Requiere revisión',
    import: 'Codificación importada',
  };

  const busy = mutation.isPending;

  async function changeSource(job: () => Promise<unknown>) {
    const result = await job();
    await qc.invalidateQueries();
    return result;
  }

  const statuses: Record<string, string> = {
    queued: 'En cola',
    running: 'Codificando',
    paused: 'Pausada',
    completed: 'Terminada',
    completed_with_errors: 'Terminada con errores',
    cancelled: 'Cancelada',
  };

  function submit(job: () => Promise<unknown>) {
    setNotice('');
    mutation.mutate(job);
  }

  async function download(id: number, format: string) {
    const blob = await api.export(id, format);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `base-codificada-${id}.${format === 'csv' ? 'zip' : 'xlsx'}`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Ejecutar codificación para una sola pregunta directamente
  const handleRunSingleBinding = (bindingId: number) => {
    if (activeRun) {
      setNotice('Ya hay una ejecución activa en curso. Espere a que termine o cancélela primero.');
      setRunId(activeRun.id);
      return;
    }
    submit(async () => {
      const r = await api.start(survey.id, {
        binding_ids: [bindingId],
        mode: 'new_changed',
        use_ai: useAI,
        request_key: safeUUID(),
      });
      setRunId(r.id);
      return r;
    });
  };

  return (
    <div className="space-y-4">
      {[estimate.error, mutation.error, runQ.error, runs.error, decisionQ.error, releases.error, frequencies.error]
        .filter(Boolean)
        .map((e, i) => (
          <ErrorBanner key={i} message={(e as Error).message} />
        ))}
      {mutation.error && String((mutation.error as Error).message).includes('ejecución activa') && (
        <div className="p-3 bg-red-50 border border-red-300 rounded text-xs text-red-900 flex flex-wrap items-center justify-between gap-2">
          <span>Hay una ejecución activa bloqueando el inicio de una nueva codificación.</span>
          <button
            type="button"
            className="btn-sec text-xs text-estado-critico font-medium"
            disabled={busy}
            onClick={handleCancelActive}
          >
            Cancelar ejecución activa ahora
          </button>
        </div>
      )}
      {notice && <OkBanner message={notice} />}

      {/* Bloque 1: Codificar preguntas o toda la encuesta */}
      {(section === 'runs' || section === 'all') && can('code') && (
        <Card>
          <div className="space-y-3">
            <div>
              <h2 className="text-lg font-semibold text-tinta">Codificar preguntas o toda la encuesta</h2>
              <p className="text-xs text-tinta-3 mt-0.5">
                Procesa las respuestas textuales de las preguntas vinculadas contra sus matrices de códigos. Las correcciones manuales quedan protegidas.
              </p>
            </div>

            {activeRun && (
              <div className="p-3 bg-amber-50 border border-amber-300 rounded text-xs text-amber-900 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <strong>Ejecución en curso:</strong> estado «{statuses[activeRun.status] ?? activeRun.status}» · {activeRun.finished}/{activeRun.total} respuestas procesadas.
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="btn-sec text-xs px-2.5 py-1"
                    onClick={() => setRunId(activeRun.id)}
                  >
                    Ver detalles
                  </button>
                  <button
                    type="button"
                    className="btn-sec text-xs text-estado-critico px-2.5 py-1"
                    disabled={busy}
                    onClick={handleCancelActive}
                  >
                    Cancelar ejecución activa
                  </button>
                </div>
              </div>
            )}

            {!survey.bindings.length ? (
              <div className="p-4 bg-plano border border-grid rounded text-xs text-tinta-2 space-y-3">
                <p className="font-semibold text-tinta">Aún no hay preguntas vinculadas a matrices de codificación.</p>
                <p>
                  Para codificar respuestas, primero asigne el tratamiento <strong>«Codificar con matriz»</strong> a las variables deseadas en la pestaña <strong>«Tratamiento de variables»</strong>.
                </p>
                {onNavigate && (
                  <button
                    type="button"
                    className="btn-sec text-xs min-h-[44px] px-3"
                    onClick={() => onNavigate('plan')}
                  >
                    Ir a Tratamiento de variables (Plan) →
                  </button>
                )}
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-xs font-semibold text-tinta mb-1.5">
                    Seleccionar preguntas a codificar (sin selección se procesan todas las {survey.bindings.length}):
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 p-2 bg-plano rounded border border-grid max-h-40 overflow-y-auto">
                    {survey.bindings.map((b) => (
                      <label key={b.id} className="flex items-center gap-2 text-xs cursor-pointer p-1 rounded hover:bg-superficie">
                        <input
                          type="checkbox"
                          className="rounded text-marca"
                          checked={selected.includes(b.id)}
                          onChange={(e) => {
                            setSelected(e.target.checked ? [...selected, b.id] : selected.filter((id) => id !== b.id));
                            estimate.reset();
                          }}
                        />
                        <span className="font-medium text-tinta break-words">{b.variable}</span>
                        <span className="text-[11px] text-tinta-3 font-mono">→ {b.output_name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="etiqueta">
                    Modo de procesamiento:
                    <select
                      className="input"
                      value={mode}
                      onChange={(e) => {
                        setMode(e.target.value);
                        estimate.reset();
                      }}
                    >
                      <option value="new_changed">Nuevos y modificados (recomendado)</option>
                      <option value="new">Solo casos nuevos</option>
                      <option value="retry">Reintentar fallidos o sin categoría</option>
                      <option value="recode">Recodificar todo en una nueva ejecución</option>
                    </select>
                  </label>

                  <div className="flex flex-col justify-end">
                    <label className="flex items-center gap-2 text-xs text-tinta cursor-pointer py-2">
                      <input
                        type="checkbox"
                        checked={useAI}
                        onChange={(e) => {
                          setAI(e.target.checked);
                          estimate.reset();
                        }}
                      />
                      <span>Usar IA para respuestas sin regla inequívoca (consume presupuesto)</span>
                    </label>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 items-center pt-1">
                  <button
                    type="button"
                    className="btn-sec text-xs"
                    disabled={estimate.isPending}
                    onClick={() => estimate.mutate()}
                  >
                    {estimate.isPending ? 'Estimando…' : 'Estimar trabajo y coste'}
                  </button>
                  <button
                    type="button"
                    className="btn text-xs"
                    disabled={busy || !survey.enabled || !!activeRun}
                    title={activeRun ? "Hay una ejecución activa en curso. Espere a que termine o cancélela para iniciar otra." : ""}
                    onClick={() =>
                      submit(async () => {
                        const r = await api.start(survey.id, request());
                        setRunId(r.id);
                        return r;
                      })
                    }
                  >
                    {busy ? 'Iniciando…' : activeRun ? 'Ejecución en curso…' : 'Ejecutar codificación'}
                  </button>
                </div>

                {estimate.data && (
                  <div className="p-3 bg-plano rounded border border-grid space-y-1 text-xs text-tinta-2">
                    <p className="font-semibold text-tinta">Estimación de la ejecución:</p>
                    <p>
                      {estimate.data.tasks} respuestas por procesar · {estimate.data.summary?.reused ?? 0} conservadas · hasta {estimate.data.possible_ai_calls} llamadas a IA.
                    </p>
                    <p>
                      Coste aproximado: ${estimate.data.estimated_cost?.toFixed(4) ?? '0.0000'} USD. {estimate.data.warning}
                    </p>
                  </div>
                )}
              </>
            )}

            {!survey.enabled && (
              <InfoBanner message="La codificación está pausada en esta encuesta. Marque «Habilitar codificación en esta encuesta» en el bloque superior para poder ejecutar." />
            )}

            {/* Selector de ejecuciones y progreso en vivo */}
            <div className="pt-2 border-t border-grid space-y-2">
              <label className="etiqueta">
                Historial de ejecuciones guardadas:
                <select className="input" value={runId} onChange={(e) => setRunId(e.target.value)}>
                  <option value="">Seleccionar ejecución…</option>
                  {runs.data?.map((r) => (
                    <option key={r.id} value={r.id}>
                      {statuses[r.status] ?? r.status} · {r.finished}/{r.total} respuestas ({new Date(r.created_at || '').toLocaleTimeString()})
                    </option>
                  ))}
                </select>
              </label>

              {runQ.data && (
                <div className="p-3 bg-plano rounded border border-grid space-y-2 text-xs">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-tinta">
                      Estado: {statuses[runQ.data.status] ?? runQ.data.status}
                    </span>
                    <span className="font-mono text-tinta-2">
                      {runQ.data.finished} / {runQ.data.total} procesadas ({Math.round(runQ.data.progress * 100)}%)
                    </span>
                  </div>
                  <progress aria-label="Avance de codificación" className="w-full h-2 rounded overflow-hidden" max="1" value={runQ.data.progress} />
                  <div className="flex flex-wrap gap-2 pt-1">
                    {['queued', 'running'].includes(runQ.data.status) && (
                      <button className="btn-sec text-xs" disabled={busy} onClick={() => submit(() => api.control(runId, 'pause'))}>
                        Pausar tras la respuesta actual
                      </button>
                    )}
                    {runQ.data.status === 'paused' && (
                      <button className="btn text-xs" disabled={busy} onClick={() => submit(() => api.control(runId, 'resume'))}>
                        Reanudar codificación
                      </button>
                    )}
                    {['queued', 'running', 'paused'].includes(runQ.data.status) && (
                      <button className="btn-sec text-xs text-estado-critico" disabled={busy} onClick={() => submit(() => api.control(runId, 'cancel'))}>
                        Cancelar pendientes
                      </button>
                    )}
                  </div>
                  {runQ.data.errors?.map((e, idx) => (
                    <ErrorBanner key={idx} message={`Respuesta ${e.task_id}: ${e.message}`} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Bloque 2: Revisión y resultados por pregunta */}
      {(section === 'review' || section === 'all') && (
        <Card>
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-tinta">Revisión y resultados por pregunta</h2>
              <p className="text-xs text-tinta-3 mt-0.5">
                Supervise y audite las decisiones tomadas por las reglas de la matriz o IA. Puede reclasificar manualmente cualquier respuesta y protegerla de sobreescrituras.
              </p>
            </div>

            {!survey.bindings.length ? (
              <div className="p-4 bg-plano border border-grid rounded text-xs text-tinta-2 space-y-3">
                <p className="font-semibold text-tinta">No hay preguntas vinculadas a matrices de codificación.</p>
                <p>
                  Para revisar decisiones o resultados, primero asigne el tratamiento <strong>«Codificar con matriz»</strong> a las variables abiertas en el plan de variables.
                </p>
                {onNavigate && (
                  <button
                    type="button"
                    className="btn-sec text-xs min-h-[44px] px-3"
                    onClick={() => onNavigate('plan')}
                  >
                    Ir a Tratamiento de variables (Plan) →
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-3 items-end">
                  <label className="etiqueta flex-1 min-w-[240px]">
                    Pregunta a revisar:
                  <select
                    className="input"
                    value={binding}
                    onChange={(e) => {
                      setBinding(e.target.value);
                      setOffset(0);
                      setReview(null);
                    }}
                  >
                    <option value="">Seleccione una pregunta vinculada…</option>
                    {survey.bindings.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.variable} → {b.output_name} (Matriz ID #{b.version_id})
                      </option>
                    ))}
                  </select>
                </label>

                {binding && (
                  <label className="flex items-center gap-2 text-xs text-tinta cursor-pointer pb-2">
                    <input
                      type="checkbox"
                      checked={pendingOnly}
                      onChange={(e) => {
                        setPending(e.target.checked);
                        setOffset(0);
                      }}
                    />
                    <span>Solo pendientes de revisión</span>
                  </label>
                )}
              </div>

              {can('originals') && binding && activeBinding && (
                <div className="space-y-3 pt-2 border-t border-grid">
                  {decisionQ.isLoading && (
                    <div className="p-4 text-center text-xs text-tinta-2 animate-pulse bg-plano rounded">
                      Consultando decisiones de codificación…
                    </div>
                  )}

                  {/* Si no hay decisiones generadas aún */}
                  {!decisionQ.isLoading && decisionQ.data?.total === 0 && (
                    <div className="space-y-3">
                      <div className="p-3 bg-plano border border-grid rounded text-xs text-tinta-2 space-y-2">
                        <p className="font-semibold text-tinta">
                          {pendingOnly
                            ? 'No hay decisiones pendientes de revisión con este filtro.'
                            : 'Esta pregunta está vinculada pero aún no se ha ejecutado la codificación.'}
                        </p>
                        <p>
                          {pendingOnly
                            ? 'Desmarque la casilla «Solo pendientes de revisión» para ver todas las respuestas codificadas.'
                            : 'Puede iniciar la codificación específicamente para esta pregunta o revisar las respuestas de muestra abajo.'}
                        </p>
                        {!pendingOnly && can('code') && (
                          <button
                            type="button"
                            className="btn text-xs"
                            disabled={busy || !survey.enabled}
                            onClick={() => handleRunSingleBinding(activeBinding.id)}
                          >
                            ⚡ Ejecutar codificación para «{activeBinding.variable}»
                          </button>
                        )}
                      </div>

                      {/* Respuestas reales de ODK como vista previa */}
                      <div>
                        <h4 className="text-xs font-semibold text-tinta mb-1.5">
                          Respuestas registradas en campo para «{activeBinding.variable}»:
                        </h4>
                        <QuestionSampleResponses surveyId={survey.id} variable={activeBinding.variable} />
                      </div>
                    </div>
                  )}

                  {/* Si hay decisiones registradas */}
                  {decisionQ.data && decisionQ.data.total > 0 && (
                    <div className="space-y-3">
                      <div className="flex flex-wrap justify-between items-center text-xs text-tinta-2 font-medium px-1">
                        <span>
                          {decisionQ.data.total} decisiones registradas {pendingOnly ? '(filtradas: solo pendientes)' : ''}
                        </span>
                        <span className="text-tinta-3">
                          Mostrando {offset + 1}–{Math.min(offset + 25, decisionQ.data.total)}
                        </span>
                      </div>

                      <div className="space-y-2">
                        {decisionQ.data.items.map((d) => {
                          const stateClass =
                            d.response_state === 'VALIDA'
                              ? 'bg-estado-exito/15 text-estado-exito'
                              : d.response_state === 'SIN_CATEGORIA' || d.response_state === 'PENDIENTE'
                              ? 'bg-estado-aviso/15 text-estado-aviso'
                              : 'bg-plano text-tinta-3';

                          return (
                            <div key={d.id} className="border border-grid rounded-md p-3 bg-plano/40 hover:bg-plano space-y-2 text-xs">
                              <div className="flex flex-wrap items-start justify-between gap-2">
                                <p className="font-medium text-tinta break-words whitespace-pre-wrap flex-1 text-sm">
                                  {d.text || <span className="italic text-tinta-3">(respuesta en blanco)</span>}
                                </p>
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className={`px-2 py-0.5 rounded font-semibold text-[11px] ${stateClass}`}>
                                    {stateLabels[d.response_state] ?? d.response_state}
                                  </span>
                                  {d.protected ? (
                                    <span className="px-2 py-0.5 rounded bg-marca/15 text-marca font-medium text-[11px]">
                                      🛡️ Revisión protegida
                                    </span>
                                  ) : (
                                    <span className="text-tinta-3 text-[11px]">
                                      {methodLabels[d.method] ?? d.method}
                                    </span>
                                  )}
                                  {can('review') && (
                                    <button
                                      type="button"
                                      className="btn-sec text-xs py-0.5 px-2"
                                      onClick={() => {
                                        setReview(d);
                                        setReason('');
                                      }}
                                    >
                                      Revisar / Corregir
                                    </button>
                                  )}
                                </div>
                              </div>

                              {/* Categorías asignadas */}
                              <div className="flex flex-wrap gap-1 items-center pt-1 border-t border-grid/60">
                                <span className="text-tinta-3 text-[11px]">Categorías asignadas:</span>
                                {d.codes.length === 0 ? (
                                  <span className="text-tinta-3 italic">Ninguna</span>
                                ) : (
                                  d.codes.map((c) => (
                                    <span
                                      key={c}
                                      className="px-2 py-0.5 rounded bg-superficie border border-grid text-tinta font-mono text-[11px]"
                                      title={codeLabels[c] ? `${c}: ${codeLabels[c]}` : c}
                                    >
                                      {c}{codeLabels[c] ? ` · ${codeLabels[c]}` : ''}
                                    </span>
                                  ))
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Paginación */}
                      <div className="flex flex-wrap gap-2 items-center justify-between pt-2">
                        <button
                          type="button"
                          className="btn-sec text-xs"
                          disabled={!offset}
                          onClick={() => setOffset(Math.max(0, offset - 25))}
                        >
                          ← Anteriores
                        </button>
                        <span className="text-xs text-tinta-3">
                          Página {Math.floor(offset / 25) + 1} de {Math.ceil(decisionQ.data.total / 25)}
                        </span>
                        <button
                          type="button"
                          className="btn-sec text-xs"
                          disabled={offset + 25 >= decisionQ.data.total}
                          onClick={() => setOffset(offset + 25)}
                        >
                          Siguientes →
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Modal / formulario de revisión manual */}
              {review && (
                <form
                  className="border-2 border-marca/40 bg-superficie p-4 mt-3 rounded-md space-y-3 shadow-md"
                  onSubmit={(e) => {
                    e.preventDefault();
                    submit(async () => {
                      const r = await api.review(review.id, {
                        response_state: review.response_state,
                        codes: review.codes,
                        evidence: {},
                        rationale: reason,
                      });
                      setReview(null);
                      return r;
                    });
                  }}
                >
                  <div className="flex justify-between items-center border-b border-grid pb-2">
                    <h3 className="font-semibold text-sm text-tinta">Revisión manual de respuesta</h3>
                    <button
                      type="button"
                      className="text-xs text-tinta-3 hover:text-tinta"
                      onClick={() => setReview(null)}
                    >
                      ✕ Cerrar
                    </button>
                  </div>

                  <div className="p-3 bg-plano rounded border border-grid text-xs space-y-1">
                    <span className="text-tinta-3 font-semibold">Texto original del encuestado:</span>
                    <p className="font-medium text-tinta break-words whitespace-pre-wrap text-sm">{review.text}</p>
                  </div>

                  <label className="etiqueta">
                    Estado de la respuesta:
                    <select
                      className="input text-xs"
                      value={review.response_state}
                      onChange={(e) =>
                        setReview({
                          ...review,
                          response_state: e.target.value,
                          codes: e.target.value === 'VALIDA' ? review.codes : [],
                        })
                      }
                    >
                      {['VALIDA', 'NS', 'NINGUNO', 'RECHAZO', 'VACIA', 'NO_APLICA', 'SIN_CATEGORIA'].map((s) => (
                        <option key={s} value={s}>
                          {stateLabels[s]}
                        </option>
                      ))}
                    </select>
                  </label>

                  {review.response_state === 'VALIDA' && (
                    <div className="space-y-2">
                      <span className="block text-xs font-semibold text-tinta">
                        Seleccione una o más categorías aplicables:
                      </span>
                      {reviewCodes && reviewCodes.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 p-2.5 bg-plano rounded border border-grid max-h-48 overflow-y-auto">
                          {reviewCodes.map((c) => (
                            <label
                              key={c.value}
                              className="flex items-start gap-2 text-xs p-1.5 rounded hover:bg-superficie cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                className="rounded text-marca mt-0.5"
                                checked={review.codes.includes(c.value)}
                                onChange={(e) =>
                                  setReview({
                                    ...review,
                                    codes: e.target.checked
                                      ? [...review.codes, c.value]
                                      : review.codes.filter((x) => x !== c.value),
                                  })
                                }
                              />
                              <div className="min-w-0">
                                <span className="font-mono font-semibold text-tinta">{c.value}</span>
                                <span className="text-tinta ml-1 font-medium">{c.label}</span>
                                {c.definition && (
                                  <p className="text-[11px] text-tinta-3 truncate" title={c.definition}>
                                    {c.definition}
                                  </p>
                                )}
                              </div>
                            </label>
                          ))}
                        </div>
                      ) : (
                        <InfoBanner message="No se encontraron categorías en la matriz para esta pregunta." />
                      )}
                    </div>
                  )}

                  <label className="etiqueta">
                    Motivo o justificación de la revisión manual:
                    <textarea
                      className="input text-xs"
                      rows={2}
                      required
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Explique el criterio aplicado para clasificar o corregir esta respuesta…"
                    />
                  </label>

                  <div className="flex flex-wrap gap-2 pt-1">
                    <button type="submit" className="btn text-xs" disabled={busy}>
                      {busy ? 'Guardando…' : 'Guardar revisión protegida'}
                    </button>
                    <button
                      type="button"
                      className="btn-sec text-xs"
                      onClick={() => setReview(null)}
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
      </Card>
      )}

      {/* Bloque 3: Bases publicadas */}
      {(section === 'releases' || section === 'all') && (
      <Card>
        <div className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-tinta">Bases codificadas publicadas</h2>
            <p className="text-xs text-tinta-3 mt-0.5">
              Cada corte conserva los datos y decisiones de ese momento. Publicar un nuevo corte no altera los anteriores.
            </p>
          </div>

          {can('publish') && (
            <form
              className="space-y-3 p-3 bg-plano rounded border border-grid"
              onSubmit={(e) => {
                e.preventDefault();
                submit(() =>
                  api.publish(survey.id, {
                    name: releaseName,
                    accept_unreviewed: acceptUnreviewed,
                    accept_incomplete: acceptIncomplete,
                    reason: releaseReason,
                  })
                );
              }}
            >
              <h3 className="text-xs font-semibold text-tinta">Publicar nuevo corte de base codificada</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="etiqueta">
                  Nombre del corte:
                  <input
                    required
                    className="input"
                    value={releaseName}
                    onChange={(e) => setReleaseName(e.target.value)}
                    placeholder="ej. Corte final Septiembre · v1"
                  />
                </label>

                <div className="space-y-1 pt-1">
                  <label className="flex items-center gap-2 text-xs text-tinta cursor-pointer">
                    <input
                      type="checkbox"
                      checked={acceptUnreviewed}
                      onChange={(e) => setAcceptUnreviewed(e.target.checked)}
                    />
                    <span>Aceptar decisiones aún sin revisión humana</span>
                  </label>
                  <label className="flex items-center gap-2 text-xs text-tinta cursor-pointer">
                    <input
                      type="checkbox"
                      checked={acceptIncomplete}
                      onChange={(e) => setAcceptIncomplete(e.target.checked)}
                    />
                    <span>Publicar corte parcial con pendientes</span>
                  </label>
                </div>
              </div>

              {(acceptIncomplete || acceptUnreviewed) && (
                <label className="etiqueta">
                  Motivo o justificación de publicación excepcional:
                  <textarea
                    required
                    className="input text-xs"
                    rows={2}
                    value={releaseReason}
                    onChange={(e) => setReleaseReason(e.target.value)}
                    placeholder="Indique por qué se autoriza la publicación con pendientes o sin revisión manual…"
                  />
                </label>
              )}

              <button type="submit" className="btn text-xs" disabled={busy || !releaseName.trim()}>
                {busy ? 'Publicando…' : 'Publicar base codificada'}
              </button>
            </form>
          )}

          {!releases.data?.length ? (
            <InfoBanner message="Todavía no hay cortes publicados. Ejecute la codificación, audite los resultados y publique el primer corte." />
          ) : (
            <div className="space-y-2">
              {releases.data.map((r) => (
                <div key={r.id} className="border border-grid rounded-md p-3 bg-plano/40 space-y-2 text-xs">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <strong className="text-sm text-tinta">{r.name}</strong>
                      <p className="text-tinta-2">
                        {r.cases} casos · {r.missing} pendientes · {r.unreviewed} sin revisión manual
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-2 items-center">
                      <button
                        type="button"
                        className={`btn-sec text-xs ${releaseId === String(r.id) ? 'bg-marca text-blanco font-semibold' : ''}`}
                        onClick={() => setReleaseId(String(r.id))}
                      >
                        {releaseId === String(r.id) ? 'Ocultar frecuencias' : 'Ver frecuencias'}
                      </button>

                      {can('publish') && (
                        <button
                          type="button"
                          className="btn-sec text-xs"
                          disabled={busy}
                          onClick={() => submit(() => changeSource(() => api.activate(r.id)))}
                        >
                          Usar en análisis de Analista
                        </button>
                      )}

                      {can('export') && (
                        <>
                          <button
                            type="button"
                            className="btn-sec text-xs"
                            disabled={busy}
                            onClick={() => submit(() => download(r.id, 'xlsx'))}
                          >
                            Descargar Excel
                          </button>
                          <button
                            type="button"
                            className="btn-sec text-xs"
                            disabled={busy}
                            onClick={() => submit(() => download(r.id, 'csv'))}
                          >
                            Descargar CSV y diccionario
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Detalle de frecuencias si está seleccionado este corte */}
                  {releaseId === String(r.id) && (
                    <div className="pt-3 border-t border-grid space-y-3 bg-superficie p-3 rounded">
                      <div className="flex flex-wrap justify-between items-center gap-2">
                        <h4 className="font-semibold text-tinta">
                          Frecuencias de categorías · Corte «{r.name}»
                        </h4>
                        <div className="flex flex-wrap gap-2 items-center">
                          <label className="text-xs text-tinta-2 flex items-center gap-1">
                            Base:
                            <select
                              className="input text-xs py-1"
                              value={base}
                              onChange={(e) => setBase(e.target.value)}
                            >
                              <option value="validas">Respuestas válidas</option>
                              <option value="total">Total de casos</option>
                              <option value="menciones">Menciones</option>
                            </select>
                          </label>
                        </div>
                      </div>

                      {!binding ? (
                        <InfoBanner message="Seleccione una pregunta vinculada en el bloque anterior para visualizar su tabla de frecuencias." />
                      ) : frequencies.isLoading ? (
                        <div className="p-3 text-center text-xs text-tinta-3 animate-pulse">Cargando frecuencias…</div>
                      ) : frequencies.data ? (
                        <div className="space-y-2">
                          <p className="text-tinta-2">
                            {frequencies.data.n} respuestas sustantivas · base {frequencies.data.n_base} · cobertura {(frequencies.data.coverage * 100).toFixed(1)}%
                          </p>
                          {frequencies.data.warning && <InfoBanner message={frequencies.data.warning} />}

                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="border-b border-grid text-left text-tinta-3">
                                  <th className="p-2">Categoría</th>
                                  <th className="p-2 text-center">Frecuencia (n)</th>
                                  <th className="p-2 text-center">Porcentaje (%)</th>
                                  <th className="p-2 text-center">Denominador</th>
                                </tr>
                              </thead>
                              <tbody>
                                {frequencies.data.categories.map((c) => (
                                  <tr key={c.code} className="border-b border-grid/50 hover:bg-plano/50">
                                    <td className="p-2 font-medium text-tinta">
                                      <span className="font-mono text-tinta-3 mr-1">[{c.code}]</span> {c.label}
                                    </td>
                                    <td className="p-2 text-center">{c.n}</td>
                                    <td className="p-2 text-center font-bold text-tinta">
                                      {c.percent != null ? `${c.percent.toFixed(1)}%` : '—'}
                                    </td>
                                    <td className="p-2 text-center text-tinta-3">{c.denominator}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          <div className="text-[11px] text-tinta-3 pt-1 border-t border-grid flex flex-wrap gap-2">
                            <span>Desglose por estado:</span>
                            {Object.entries(frequencies.data.states).map(([k, v]) => (
                              <span key={k} className="px-1.5 py-0.5 rounded bg-plano border border-grid">
                                {stateLabels[k] ?? k}: <strong>{v}</strong>
                              </span>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {can('publish') && survey.options.analysis_release_id != null && (
            <button
              type="button"
              className="btn-sec text-xs"
              disabled={busy}
              onClick={() => submit(() => changeSource(() => api.original(survey.id)))}
            >
              Volver a datos originales para los análisis generales
            </button>
          )}
        </div>
      </Card>
      )}
    </div>
  );
}
